import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, type AuthSession } from "@/lib/api";
import { EmptyState, ErrorBox, Spinner } from "@/components/ui";

export function AuthSessionsPage() {
  const { id = "" } = useParams<{ id: string }>();
  const [sessions, setSessions] = useState<AuthSession[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [userFilter, setUserFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.listAuthSessions(id, {
        active: showAll ? "all" : true,
        user_id: userFilter.trim() || undefined,
      });
      setSessions(data);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [id, showAll, userFilter]);

  useEffect(() => {
    if (!id) return;
    void load();
  }, [id, load]);

  async function revoke(s: AuthSession) {
    try {
      await api.revokeAuthSession(id, s.id);
      await load();
    } catch (e) {
      setError(e);
    }
  }

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs text-text-muted">
          User id
          <input
            className="input min-w-[16rem] font-mono text-xs"
            value={userFilter}
            onChange={(e) => setUserFilter(e.target.value)}
            placeholder="Filter by user UUID"
          />
        </label>
        <label className="flex items-center gap-2 text-sm text-text-muted">
          <input
            type="checkbox"
            checked={showAll}
            onChange={(e) => setShowAll(e.target.checked)}
          />
          Include revoked / expired
        </label>
        <button
          type="button"
          className="btn-secondary !px-3 !py-1.5 text-xs"
          onClick={() => void load()}
        >
          Refresh
        </button>
      </div>

      {error ? (
        <div className="mb-4">
          <ErrorBox error={error} />
        </div>
      ) : null}

      {loading ? (
        <Spinner center />
      ) : sessions.length === 0 ? (
        <EmptyState title="No sessions">
          Living refresh-token sessions appear here after end users sign in.
          Token hashes are never returned by the API.
        </EmptyState>
      ) : (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Session</th>
                <th>User</th>
                <th>Status</th>
                <th>Created</th>
                <th>Expires</th>
                <th>User agent</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {sessions.map((s) => (
                <tr key={s.id}>
                  <td className="font-mono text-xs" title={s.id}>
                    {s.id.slice(0, 8)}…
                  </td>
                  <td>
                    <div className="text-sm">
                      {s.email ?? s.username ?? "—"}
                    </div>
                    <div className="font-mono text-[11px] text-text-muted" title={s.user_id}>
                      {s.user_id.slice(0, 8)}…
                    </div>
                  </td>
                  <td>
                    <span
                      className={
                        s.active
                          ? "mono-chip border-success bg-success-container text-success"
                          : "mono-chip"
                      }
                    >
                      {s.active ? "active" : s.revoked_at ? "revoked" : "expired"}
                    </span>
                  </td>
                  <td className="text-text-muted text-xs">
                    {new Date(s.created_at).toLocaleString()}
                  </td>
                  <td className="text-text-muted text-xs">
                    {new Date(s.expires_at).toLocaleString()}
                  </td>
                  <td className="max-w-[12rem] truncate text-xs text-text-muted" title={s.user_agent ?? ""}>
                    {s.user_agent ?? "—"}
                  </td>
                  <td>
                    {s.active ? (
                      <button
                        type="button"
                        className="btn-secondary !px-2 !py-1 text-xs"
                        onClick={() => void revoke(s)}
                      >
                        Revoke
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
